import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Download, LogOut } from "lucide-react";

// Custom hooks
import { useAdminAuth, useAdminData, useAdminExport } from "@/hooks/useAdmin";

// Components
import AdminLoginForm from "@/components/admin/AdminLoginForm";
import AdminStats from "@/components/admin/AdminStats";
import AnalyticsDashboard from "@/components/admin/AnalyticsDashboard";
import ReferralsTable from "@/components/admin/ReferralsTable";
import AmbassadorsTable from "@/components/admin/AmbassadorsTable";
import CodesManager from "@/components/admin/CodesManager";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_nordic-cruise-launch/artifacts/ae74l9ew_DUNDER%20LOGO%20SMOKE.png";

export default function AdminPage() {
  const [inputPassword, setInputPassword] = useState("");
  const [activeTab, setActiveTab] = useState("analytics");
  
  const { 
    isAuthenticated, 
    password, 
    isLoading: authLoading, 
    login, 
    logout 
  } = useAdminAuth();
  
  const { 
    referrals, 
    ambassadors, 
    stats, 
    analytics,
    codes,
    isLoading: dataLoading,
    isAnalyticsLoading,
    analyticsDays,
    changeAnalyticsRange,
    createCode,
    deleteCode,
    updateAmbassadorStatus 
  } = useAdminData(password, isAuthenticated);
  
  const { 
    isExporting, 
    exportReferrals, 
    exportAmbassadors 
  } = useAdminExport();

  const handleLogin = async (e) => {
    e.preventDefault();
    await login(inputPassword);
  };

  const handleLogout = () => {
    logout();
    setInputPassword("");
  };

  const handleUpdateStatus = (ambassadorId, newStatus, customCode = null) => {
    return updateAmbassadorStatus(ambassadorId, newStatus, password, customCode);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Login View
  if (!isAuthenticated) {
    return (
      <AdminLoginForm
        password={inputPassword}
        setPassword={setInputPassword}
        onSubmit={handleLogin}
        isLoading={authLoading}
      />
    );
  }

  // Dashboard View
  return (
    <div className="min-h-screen bg-[#050505] p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <header className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div className="flex items-center gap-4">
            <img 
              src={LOGO_URL} 
              alt="Dunderkryssningen Logo"
              className="w-24 md:w-32"
              data-testid="dashboard-logo"
            />
            <div>
              <h1 className="font-heading text-2xl md:text-3xl text-white uppercase">
                Admin Dashboard
              </h1>
              <p className="text-gray-500 text-sm">Hantera biljettköp & ambassadörer</p>
            </div>
          </div>
          
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-[#333] text-gray-400 hover:text-white hover:border-[#FFD700] rounded-sm"
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logga ut
          </Button>
        </header>

        {/* Stats */}
        <AdminStats stats={stats} />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <TabsList className="bg-[#1A1A1A] border border-[#222]">
              <TabsTrigger 
                value="analytics" 
                className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black"
                data-testid="tab-analytics"
              >
                Analys
              </TabsTrigger>
              <TabsTrigger 
                value="referrals" 
                className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black"
                data-testid="tab-referrals"
              >
                Biljettköp ({referrals.length})
              </TabsTrigger>
              <TabsTrigger 
                value="ambassadors" 
                className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black"
                data-testid="tab-ambassadors"
              >
                Ambassadörer ({ambassadors.length})
              </TabsTrigger>
              <TabsTrigger 
                value="codes" 
                className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black"
                data-testid="tab-codes"
              >
                Koder ({codes.length})
              </TabsTrigger>
            </TabsList>

            {(activeTab === "referrals" || activeTab === "ambassadors") && (
              <ExportButton
                activeTab={activeTab}
                isExporting={isExporting}
                onExportReferrals={() => exportReferrals(password)}
                onExportAmbassadors={() => exportAmbassadors(password)}
              />
            )}

            {activeTab === "analytics" && (
              <RangeFilter
                activeDays={analyticsDays}
                onChange={(days) => changeAnalyticsRange(days, password)}
              />
            )}
          </div>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <AnalyticsDashboard analytics={analytics} isLoading={dataLoading || isAnalyticsLoading} />
          </TabsContent>


          {/* Referrals Tab */}
          <TabsContent value="referrals">
            <div className="bg-[#0A0A0A] border border-[#222] overflow-hidden">
              <div className="overflow-x-auto">
                <ReferralsTable 
                  referrals={referrals} 
                  isLoading={dataLoading}
                  formatDate={formatDate}
                />
              </div>
            </div>
          </TabsContent>

          {/* Ambassadors Tab */}
          <TabsContent value="ambassadors">
            <div className="bg-[#0A0A0A] border border-[#222] overflow-hidden">
              <div className="overflow-x-auto">
                <AmbassadorsTable 
                  ambassadors={ambassadors} 
                  isLoading={dataLoading}
                  onUpdateStatus={handleUpdateStatus}
                  formatDate={formatDate}
                />
              </div>
            </div>
          </TabsContent>

          {/* Codes Tab */}
          <TabsContent value="codes">
            <CodesManager
              codes={codes}
              isLoading={dataLoading}
              onCreate={(code, name) => createCode(code, name, password)}
              onDelete={(code) => deleteCode(code, password)}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function RangeFilter({ activeDays, onChange }) {
  const ranges = [
    { label: "7 dagar", value: 7 },
    { label: "30 dagar", value: 30 },
    { label: "Allt", value: null },
  ];
  return (
    <div className="flex items-center gap-1 bg-[#1A1A1A] border border-[#222] p-1 rounded-sm" data-testid="analytics-range-filter">
      {ranges.map((r) => {
        const active = activeDays === r.value;
        return (
          <button
            key={r.label}
            onClick={() => onChange(r.value)}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-sm transition-colors ${
              active ? "bg-[#FFD700] text-black" : "text-gray-400 hover:text-white"
            }`}
            data-testid={`analytics-range-${r.value ?? "all"}`}
          >
            {r.label}
          </button>
        );
      })}
    </div>
  );
}

function ExportButton({ activeTab, isExporting, onExportReferrals, onExportAmbassadors }) {
  const handleClick = activeTab === "referrals" ? onExportReferrals : onExportAmbassadors;
  const testId = activeTab === "referrals" ? "export-referrals-csv-button" : "export-ambassadors-csv-button";

  return (
    <Button
      onClick={handleClick}
      disabled={isExporting}
      className="bg-[#FFD700] text-black font-bold uppercase tracking-wider hover:bg-[#E5C100] rounded-sm"
      data-testid={testId}
    >
      {isExporting ? (
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
      ) : (
        <Download className="h-4 w-4 mr-2" />
      )}
      Exportera CSV
    </Button>
  );
}
